import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Play,
  CheckCircle,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  BookOpen,
  Send,
  MessageCircle,
  Clock3,
  ListChecks,
  User as UserIcon,
  Lock,
  FileText,
  HelpCircle,
  PartyPopper,
  Award,
  Info,
  GraduationCap,
  X,
  Menu,
  Sparkles,
  CalendarX2,
} from 'lucide-react';
import VideoPlayer from '../../components/course/VideoPlayer';
import TheoryRenderer from '../../components/course/TheoryRenderer';
import InlineQuiz from '../../components/course/InlineQuiz';
import {
  useGetPurchasedCourseContentQuery,
  useUpdateLessonProgressMutation,
  useGetMyReviewQuery,
} from '../../api/apiSlice';

const SECTION_KEY = (courseId) => `course_${courseId}_lastLesson`;

const CourseViewer = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU';
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [activeLesson, setActiveLesson] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set([0]));
  // 2 таба: 'overview' (статика — описание, инструктор, чаты) и 'learn'
  // (фокус на прохождение урока). Дефолт learn — юзер пришёл учиться.
  const [activeTab, setActiveTab] = useState('learn');
  // Mobile bottom-sheet drawer для "Содержание" — паттерн Spotify/Apple Music.
  // Скрыт на десктопе (там sidebar всегда виден).
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: courseContent, isLoading, error } = useGetPurchasedCourseContentQuery(courseId, {
    skip: !courseId || !user,
  });
  const [updateProgress] = useUpdateLessonProgressMutation();

  // Подтягиваем «мой отзыв» — нужно решить, показывать ли 50%-CTA.
  // Если уже написан pending/approved отзыв → CTA скрываем (не зовём повторно).
  // Если отзыв rejected → показываем (предложим переписать).
  const { data: myReview } = useGetMyReviewQuery(courseId, {
    skip: !courseId || !user,
  });

  const data = courseContent?.data;
  const sections = data?.sections || [];
  const totalLessons = data?.totalLessons ?? sections.reduce((s, sec) => s + (sec.lessons?.length || 0), 0);
  const completedSet = useMemo(
    () => new Set((data?.completedLessons || []).map(String)),
    [data?.completedLessons]
  );
  const progressPercent = data?.progress ?? 0;

  // Восстанавливаем последний открытый урок из localStorage,
  // или открываем первый из первой секции.
  useEffect(() => {
    if (!sections.length) return;
    if (activeLesson) return;
    const lastId = localStorage.getItem(SECTION_KEY(courseId));
    const allLessons = sections.flatMap((s) => s.lessons || []);
    const found = lastId && allLessons.find((l) => String(l._id) === lastId);
    setActiveLesson(found ? found._id : allLessons[0]?._id || null);
  }, [sections, courseId, activeLesson]);

  const handleSelectLesson = useCallback(
    (lessonId) => {
      setActiveLesson(lessonId);
      localStorage.setItem(SECTION_KEY(courseId), String(lessonId));
    },
    [courseId]
  );

  const toggleLessonComplete = useCallback(
    async (lessonId, completed) => {
      try {
        await updateProgress({ courseId, lessonId, completed }).unwrap();
        if (completed) toast.success(t('courseViewer.lessonCompleted'));
      } catch (err) {
        toast.error(err?.data?.message || t('courseViewer.progressSaveError'));
      }
    },
    [courseId, updateProgress, t]
  );

  const handleEnded = useCallback(
    (lessonId) => {
      if (!completedSet.has(String(lessonId))) {
        toggleLessonComplete(lessonId, true);
      }
    },
    [completedSet, toggleLessonComplete]
  );

  const toggleSection = useCallback((index) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, []);

  // Flat-список уроков с указателем секции — нужен для prev/next навигации.
  // Считаем один раз через useMemo, и затем находим текущий индекс.
  const flatLessons = useMemo(
    () =>
      sections.flatMap((s) =>
        (s.lessons || []).map((l) => ({ ...l, _sectionId: s._id, _sectionTitle: s.title }))
      ),
    [sections]
  );

  const currentLesson = useMemo(
    () => flatLessons.find((l) => l._id === activeLesson),
    [flatLessons, activeLesson]
  );

  const currentIndex = useMemo(
    () => flatLessons.findIndex((l) => l._id === activeLesson),
    [flatLessons, activeLesson]
  );

  // Соседние уроки. «Следующий незаблокированный» — это важный нюанс:
  // если следующий урок locked (gate не пройден), кнопка должна показать
  // именно эту причину, а не молча скакать через locked-урок.
  const prevLesson = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const nextLesson =
    currentIndex >= 0 && currentIndex < flatLessons.length - 1
      ? flatLessons[currentIndex + 1]
      : null;
  const isLastLesson = currentIndex === flatLessons.length - 1 && currentIndex >= 0;

  // Авто-переход после успешной сдачи квиза или отметки «пройдено» —
  // подскажет следующий шаг, но НЕ перематывает сам (студент должен
  // нажать кнопку, чтобы избежать «пропустил, не успел сохранить»).

  // ─── Loading / Error ─────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('courseViewer.loading')}</p>
        </div>
      </div>
    );
  }
  if (error) {
    // ACCESS_EXPIRED — отдельный экран с CalendarX иконкой и более мягким
    // тоном (не "ошибка", а "срок прошёл"). Сертификат, если был выдан раньше,
    // остаётся видимым через /my-certificates.
    const isExpired = error?.data?.code === 'ACCESS_EXPIRED';
    if (isExpired) {
      const expiresAt = error?.data?.expiresAt;
      const dateLocale = t('locale.code', { defaultValue: 'ru-RU' });
      const expiryStr = expiresAt
        ? new Date(expiresAt).toLocaleDateString(dateLocale, { day: '2-digit', month: 'long', year: 'numeric' })
        : '';
      return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-gray-50 dark:from-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
          <div className="text-center max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 sm:p-10 border border-amber-200 dark:border-amber-900/30">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <CalendarX2 className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('courseViewer.expiredTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {t('courseViewer.expiredHint')}
            </p>
            {expiryStr && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                {t('courseViewer.expiredOn', { date: expiryStr })}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={() => navigate('/my-courses')}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 font-semibold text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('courseViewer.goBack')}
              </button>
              <button
                onClick={() => navigate(`/courses/${courseId}`)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm"
              >
                {t('courseViewer.expiredRepurchase')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 dark:text-white font-semibold mb-2">{t('courseViewer.loadErrorTitle')}</p>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error?.data?.message || t('courseViewer.loadError')}
          </p>
          <button
            onClick={() => navigate('/my-courses')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('courseViewer.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const isCurrentDone = currentLesson ? completedSet.has(String(currentLesson._id)) : false;
  const watermark = user?.email || user?.name || '';

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      {/* Header — sticky сверху с тремя слоями:
            1. Title row (back + name + % desktop)
            2. Tabs row (переключатель «О курсе / Прохождение»)
            3. Тонкая полоска прогресса — всегда видна, на ЛЮБОЙ табе.
          z-20 (не z-40) чтобы при открытии sidebar-drawer (z-40 в
          DashboardLayout) header курса оказался ПОД дровером, а не сбоку. */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-white/80 supports-[backdrop-filter]:dark:bg-gray-900/80">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/my-courses')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition shrink-0"
              aria-label={t('courseViewer.ariaBack')}
            >
              <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate break-words">
                {data?.title}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {t('courseViewer.header', {
                  done: data?.completedCount ?? 0,
                  total: totalLessons,
                  percent: progressPercent,
                })}
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <div className="w-40 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
              {progressPercent}%
            </span>
          </div>
        </div>

        {/* Tabs row */}
        <div className="max-w-7xl mx-auto px-2 sm:px-4 flex gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              activeTab === 'overview'
                ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Info className="w-4 h-4" />
            <span>{t('courseViewer.tabOverview')}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('learn')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              activeTab === 'learn'
                ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <GraduationCap className="w-4 h-4" />
            <span>{t('courseViewer.tabLearn')}</span>
          </button>
        </div>

        {/* Тонкая полоска прогресса под табами — всегда видна. */}
        <div className="h-1 bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Mid-course review prompt — появляется когда юзер прошёл ≥50%,
          но ещё не оставил отзыв и курс ещё не завершён. Скрываем при:
          • progress < 50 (gate с бэка)
          • уже есть pending/approved отзыв (не зовём повторно)
          • completedAt — там завершение-баннер ниже сам зовёт оставить отзыв
          rejected-отзыв НЕ блокирует CTA — наоборот, предлагаем переписать. */}
      {!data?.completedAt &&
        progressPercent >= 50 &&
        myReview?.canReview &&
        (!myReview.review || myReview.review.status === 'rejected') && (
          <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-4">
            <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-900 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {myReview.review?.status === 'rejected'
                    ? t('courseViewer.review.rewriteTitle')
                    : t('courseViewer.review.halfwayTitle')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('courseViewer.review.hint', { percent: progressPercent })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/course/${courseId}?tab=reviews`)}
                className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-semibold transition"
              >
                {t('courseViewer.review.leave')}
              </button>
            </div>
          </div>
        )}

      {/* Completion banner — показывается когда курс пройден (completedAt
          выставлен на бэке). Сертификат уже автоматически выпущен бэк-хуком,
          поэтому даём прямые CTA: «Мои сертификаты» (скачать PDF) + «Оставить
          отзыв». Баннер иммутабельный — даже если юзер снимет галочку с урока,
          остаётся (курс был пройден когда-то). */}
      {data?.completedAt && (
        <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-4">
          <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50 via-white to-amber-50 dark:from-emerald-950/30 dark:via-gray-900 dark:to-amber-950/30 p-5 sm:p-6 shadow-sm flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg">
              <PartyPopper className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('courseViewer.completion.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {t('courseViewer.completion.subtitle', {
                  date: new Date(data.completedAt).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' }),
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate('/my-certificates')}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-semibold shadow-md transition"
              >
                <Award className="w-4 h-4" />
                {t('courseViewer.completion.certificate')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/course/${courseId}?tab=reviews`)}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 dark:border-emerald-900/50 bg-white dark:bg-gray-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 px-4 py-2.5 text-sm font-semibold transition"
              >
                <MessageCircle className="w-4 h-4" />
                {t('courseViewer.completion.review')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab: О КУРСЕ ────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <OverviewTab
          data={data}
          totalLessons={totalLessons}
          progressPercent={progressPercent}
          flatLessons={flatLessons}
          sections={sections}
          completedSet={completedSet}
          activeLesson={activeLesson}
          onContinue={() => setActiveTab('learn')}
        />
      )}

      {/* ─── Tab: ПРОХОЖДЕНИЕ ─────────────────────────────────────── */}
      {/* pb-24 на мобиле резервирует место под sticky bottom navbar + FAB. */}
      <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6 p-4 lg:p-6 pb-24 lg:pb-6 ${activeTab === 'learn' ? '' : 'hidden'}`}>
        {/* Main */}
        <div className="lg:col-span-3 space-y-6">
          {/* Главное содержимое урока — рендер зависит от type. */}
          {!currentLesson ? (
            <div className="aspect-video rounded-2xl bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400">{t('courseViewer.lesson.selectPrompt')}</p>
            </div>
          ) : currentLesson.locked ? (
            <LockedLesson lesson={currentLesson} />
          ) : currentLesson.type === 'theory' ? (
            <TheoryLesson lesson={currentLesson} />
          ) : currentLesson.type === 'quiz' ? null : (
            <VideoPlayer
              videoUrl={currentLesson.videoUrl}
              posterUrl={data?.coverImageUrl}
              title={currentLesson.title}
              watermark={watermark}
              onEnded={() => handleEnded(currentLesson._id)}
            />
          )}

          {/* Карточка урока: заголовок, тип, кнопка «пройдено» */}
          {currentLesson && !currentLesson.locked && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <LessonTypeBadge type={currentLesson.type} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">
                    {currentLesson.title}
                  </h2>
                  {currentLesson.durationMinutes ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1.5">
                      <Clock3 className="w-4 h-4" />
                      {t('courseViewer.lesson.durationMin', { n: currentLesson.durationMinutes })}
                    </p>
                  ) : null}
                </div>
                {/* Для quiz-урока «прохождение» определяется самим квизом —
                    ручную кнопку прячем, чтобы не путать. */}
                {currentLesson.type !== 'quiz' && (
                  <button
                    onClick={() => toggleLessonComplete(currentLesson._id, !isCurrentDone)}
                    className={`shrink-0 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition ${
                      isCurrentDone
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {isCurrentDone ? t('courseViewer.lesson.markDoneUndo') : t('courseViewer.lesson.markDone')}
                  </button>
                )}
              </div>

              {currentLesson.description && (
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">
                  {currentLesson.description}
                </p>
              )}

              {/* Для quiz — рендерим InlineQuiz прямо в карточке урока */}
              {currentLesson.type === 'quiz' && (
                <div className="mt-5">
                  <InlineQuiz courseId={courseId} lesson={currentLesson} />
                </div>
              )}

              {/* ── Навигация Prev/Next ────────────────────────────
                  Главная UX-просьба: «после покупки сделай кнопку
                  далее/следующий урок». Раньше юзер должен был сам
                  кликать в сайдбаре — теряли контекст, особенно на
                  мобильном где сайдбар свёрнут.
                  Логика:
                    • prev: всегда доступен если есть
                    • next: показываем причину если locked (квиз
                      впереди), либо «Курс пройден!» на последнем
                  */}
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => prevLesson && handleSelectLesson(prevLesson._id)}
                  disabled={!prevLesson}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="truncate">
                    {prevLesson ? t('courseViewer.lesson.prevPrefix', { title: prevLesson.title }) : t('courseViewer.lesson.prevEmpty')}
                  </span>
                </button>

                {isLastLesson ? (
                  <button
                    type="button"
                    onClick={() => navigate('/my-courses')}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white px-4 py-3 text-sm font-bold shadow-md"
                  >
                    <PartyPopper className="w-4 h-4" />
                    {t('courseViewer.lesson.lastDone')}
                  </button>
                ) : nextLesson?.locked ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Скроллим к ближайшему незакрытому квизу, чтобы юзер
                      // не искал «какой квиз пройти» сам. Это тот квиз, который
                      // последний по порядку перед текущей точкой и не сдан.
                      const lockingQuiz = flatLessons
                        .slice(0, flatLessons.findIndex((l) => l._id === nextLesson._id))
                        .reverse()
                        .find((l) => l.type === 'quiz' && !l.attempt?.passed);
                      if (lockingQuiz) handleSelectLesson(lockingQuiz._id);
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-4 py-3 text-sm font-medium"
                  >
                    <Lock className="w-4 h-4 shrink-0" />
                    <span className="text-center">
                      {t('courseViewer.lesson.lockedNext')}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => nextLesson && handleSelectLesson(nextLesson._id)}
                    disabled={!nextLesson}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-3 text-sm font-semibold shadow-md disabled:opacity-50"
                  >
                    <span className="truncate">
                      {nextLesson ? t('courseViewer.lesson.nextPrefix', { title: nextLesson.title }) : t('courseViewer.lesson.noLessons')}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* О курсе / Сообщество вынесены в отдельный таб "О курсе" и
              в sticky-сайдбар. Здесь оставляем только фокус на уроке. */}
        </div>

        {/* Sidebar — на мобиле скрыт (заменён на FAB + bottom-sheet ниже).
            На lg+ всегда видна Сообщество + Содержание. */}
        <aside className="hidden lg:block lg:col-span-1 space-y-4 lg:sticky lg:top-40 lg:self-start">
          {/* Карточка "Сообщество" — sticky над "Содержание", чтобы юзер
              ВСЕГДА видел чат-ссылки при просмотре любого урока. */}
          {(data?.telegramGroupUrl || data?.whatsappGroupUrl) && (
            <div className="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-800 rounded-2xl p-4 border border-indigo-200/70 dark:border-indigo-900/50">
              <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-200 mb-2 uppercase tracking-wide">
                {t('courseViewer.sidebar.communityHeader')}
              </p>
              <div className="flex flex-col gap-2">
                {data.telegramGroupUrl && (
                  <a
                    href={data.telegramGroupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition active:scale-[0.98]"
                  >
                    <Send className="w-4 h-4" /> {t('courseViewer.sidebar.telegramShort')}
                  </a>
                )}
                {data.whatsappGroupUrl && (
                  <a
                    href={data.whatsappGroupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition active:scale-[0.98]"
                  >
                    <MessageCircle className="w-4 h-4" /> {t('courseViewer.sidebar.whatsappShort')}
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 max-h-[calc(100vh-200px)] overflow-y-auto">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> {t('courseViewer.sidebar.contents')}
            </h3>

            <div className="space-y-1">
              {sections.map((section, idx) => {
                const open = expandedSections.has(idx);
                const sDone = (section.lessons || []).filter((l) => completedSet.has(String(l._id))).length;
                const sTotal = section.lessons?.length || 0;
                return (
                  <div key={section._id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <button
                      onClick={() => toggleSection(idx)}
                      className="w-full flex items-center justify-between gap-2 py-2.5 px-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition text-left"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 flex-1">
                        {section.title}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
                        {sDone}/{sTotal}
                      </span>
                      {open ? (
                        <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                      )}
                    </button>

                    {open && (
                      <div className="pl-1 space-y-0.5 pb-1">
                        {section.lessons?.map((lesson) => {
                          const done = completedSet.has(String(lesson._id));
                          const active = activeLesson === lesson._id;
                          const locked = !!lesson.locked;
                          const TypeIcon =
                            lesson.type === 'theory'
                              ? FileText
                              : lesson.type === 'quiz'
                              ? HelpCircle
                              : Play;
                          return (
                            <button
                              key={lesson._id}
                              onClick={() => handleSelectLesson(lesson._id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex items-start gap-2 ${
                                active
                                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                                  : locked
                                  ? 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              {locked ? (
                                <Lock className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                              ) : done ? (
                                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                              ) : (
                                <TypeIcon className="w-4 h-4 mt-0.5 shrink-0" />
                              )}
                              <span className="line-clamp-2 flex-1">{lesson.title}</span>
                              {lesson.type === 'quiz' && lesson.attempt?.passed && (
                                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                  ✓ {lesson.attempt.percentage}%
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1.5">
                <span>{t('courseViewer.sidebar.progress')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{progressPercent}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* ─── MOBILE: FAB + bottom-sheet "Содержание" ─────────────────
          Показываем только на табе "Прохождение" и только на мобиле.
          FAB по центру нижнего бара = всегда под большим пальцем. */}
      {activeTab === 'learn' && (
        <>
          {/* Sticky bottom navbar — Prev / Содержание / Next.
              z-20 (не z-30) чтобы sidebar-drawer (z-40) и его backdrop (z-30)
              из DashboardLayout перекрывали панель когда юзер открыл главное меню. */}
          <div
            className="lg:hidden fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex items-stretch gap-1 px-2 py-2">
              <button
                type="button"
                onClick={() => prevLesson && handleSelectLesson(prevLesson._id)}
                disabled={!prevLesson}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed active:bg-gray-100 dark:active:bg-gray-800"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('courseViewer.mobile.back')}
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex flex-col items-center justify-center gap-0.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 shadow-md active:scale-[0.98] transition"
              >
                <Menu className="w-5 h-5" />
                <span className="text-[10px] font-medium">{t('courseViewer.mobile.contents')}</span>
              </button>
              {isLastLesson ? (
                <button
                  type="button"
                  onClick={() => navigate('/my-courses')}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium text-emerald-700 dark:text-emerald-400 active:bg-emerald-50 dark:active:bg-emerald-950/30"
                >
                  <PartyPopper className="w-4 h-4" />
                  {t('courseViewer.mobile.done')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => nextLesson && !nextLesson.locked && handleSelectLesson(nextLesson._id)}
                  disabled={!nextLesson || nextLesson.locked}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed active:bg-gray-100 dark:active:bg-gray-800"
                >
                  {t('courseViewer.mobile.next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Bottom-sheet drawer "Содержание" */}
          {drawerOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex items-end" onClick={() => setDrawerOpen(false)}>
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in" />
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-h-[85vh] bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl flex flex-col"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              >
                {/* Drag handle */}
                <div className="pt-3 pb-2 flex justify-center">
                  <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
                </div>
                <div className="px-5 pb-3 flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <BookOpen className="w-5 h-5" /> {t('courseViewer.mobile.contents')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    className="p-1.5 -mr-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                    aria-label={t('courseViewer.mobile.ariaClose')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-3 pb-2 overflow-y-auto flex-1">
                  {sections.map((section, idx) => {
                    const open = expandedSections.has(idx);
                    const sDone = (section.lessons || []).filter((l) => completedSet.has(String(l._id))).length;
                    const sTotal = section.lessons?.length || 0;
                    return (
                      <div key={section._id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <button
                          onClick={() => toggleSection(idx)}
                          className="w-full flex items-center justify-between gap-2 py-3 px-2 text-left"
                        >
                          <span className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 flex-1">
                            {section.title}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
                            {sDone}/{sTotal}
                          </span>
                          {open ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
                        </button>
                        {open && (
                          <div className="pl-1 pb-2 space-y-0.5">
                            {section.lessons?.map((lesson) => {
                              const done = completedSet.has(String(lesson._id));
                              const active = activeLesson === lesson._id;
                              const locked = !!lesson.locked;
                              const TypeIcon = lesson.type === 'theory' ? FileText : lesson.type === 'quiz' ? HelpCircle : Play;
                              return (
                                <button
                                  key={lesson._id}
                                  onClick={() => {
                                    handleSelectLesson(lesson._id);
                                    setDrawerOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition flex items-start gap-2 ${
                                    active
                                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium'
                                      : locked
                                      ? 'text-gray-400 dark:text-gray-500'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {locked ? (
                                    <Lock className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                                  ) : done ? (
                                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                                  ) : (
                                    <TypeIcon className="w-4 h-4 mt-0.5 shrink-0" />
                                  )}
                                  <span className="line-clamp-2 flex-1">{lesson.title}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Overview Tab ─────────────────────────────────────────────────
// Статика курса: обложка, прогресс, инструктор, описание, чаты, структура,
// CTA "Продолжить". Юзер заходит сюда чтобы напомнить себе что за курс,
// связаться в чате, перейти к уроку.
const OverviewTab = ({
  data,
  totalLessons,
  progressPercent,
  flatLessons,
  sections,
  completedSet,
  activeLesson,
  onContinue,
}) => {
  const { t } = useTranslation();
  const currentLesson = flatLessons.find((l) => l._id === activeLesson) || flatLessons[0];
  const remainingLessons = Math.max(0, totalLessons - (data?.completedCount ?? 0));
  const typeCounts = sections.reduce(
    (acc, s) => {
      for (const l of s.lessons || []) {
        if (acc[l.type] !== undefined) acc[l.type] += 1;
      }
      return acc;
    },
    { video: 0, theory: 0, quiz: 0 }
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 sm:py-8 space-y-5 pb-24 sm:pb-8">
      {/* Cover — целиком, blur backdrop */}
      {data?.coverImageUrl && (
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800">
          <img
            src={data.coverImageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 dark:opacity-30"
          />
          <img
            src={data.coverImageUrl}
            alt={data.title}
            className="absolute inset-0 h-full w-full object-contain"
          />
        </div>
      )}

      {/* Progress card */}
      <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-900 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-gray-600 dark:text-gray-300">{t('courseViewer.overview.yourProgress')}</div>
          <div className="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
            {progressPercent}%
          </div>
        </div>
        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('courseViewer.overview.doneOf', { done: data?.completedCount ?? 0, total: totalLessons })}
          {remainingLessons > 0 && t('courseViewer.overview.remainingToCert', { count: remainingLessons })}
          {data?.completedAt && t('courseViewer.overview.courseDone')}
        </p>
      </div>

      {/* Community */}
      {(data?.telegramGroupUrl || data?.whatsappGroupUrl) && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            {t('courseViewer.sidebar.communityHeader')}
          </h3>
          <div className="flex flex-col sm:flex-row gap-2">
            {data.telegramGroupUrl && (
              <a
                href={data.telegramGroupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition active:scale-[0.98]"
              >
                <Send className="w-4 h-4" /> {t('courseViewer.sidebar.telegramGroup')}
              </a>
            )}
            {data.whatsappGroupUrl && (
              <a
                href={data.whatsappGroupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition active:scale-[0.98]"
              >
                <MessageCircle className="w-4 h-4" /> {t('courseViewer.sidebar.whatsappGroup')}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Instructor */}
      {data?.instructor && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 flex items-center gap-4">
          {data.instructor.avatarUrl ? (
            <img
              src={data.instructor.avatarUrl}
              alt={data.instructor.name}
              className="h-14 w-14 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-300 shrink-0">
              <UserIcon className="w-6 h-6" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('courseViewer.overview.instructor')}</div>
            <div className="font-semibold text-gray-900 dark:text-white truncate">
              {data.instructor.name}
            </div>
          </div>
        </div>
      )}

      {/* Description */}
      {data?.description && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Info className="w-4 h-4" /> {t('courseViewer.overview.description')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">
            {data.description}
          </p>
        </div>
      )}

      {/* Structure */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <ListChecks className="w-4 h-4" /> {t('courseViewer.overview.structure')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat icon={BookOpen} label={t('courseViewer.overview.sections')} value={sections.length} />
          <Stat icon={ListChecks} label={t('courseViewer.overview.lessons')} value={totalLessons} />
          {typeCounts.video > 0 && <Stat icon={Play} label={t('courseViewer.overview.videos')} value={typeCounts.video} />}
          {typeCounts.theory > 0 && <Stat icon={FileText} label={t('courseViewer.overview.theory')} value={typeCounts.theory} />}
          {typeCounts.quiz > 0 && <Stat icon={HelpCircle} label={t('courseViewer.overview.quizzes')} value={typeCounts.quiz} />}
        </div>
      </div>

      {/* Sticky CTA "Продолжить" */}
      {currentLesson && !data?.completedAt && (
        <div className="sticky bottom-4 z-10">
          <button
            type="button"
            onClick={onContinue}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-4 text-base font-semibold shadow-xl active:scale-[0.98] transition"
          >
            <Sparkles className="w-5 h-5" />
            <span>
              {progressPercent === 0
                ? t('courseViewer.overview.ctaStart', { title: currentLesson.title })
                : t('courseViewer.overview.ctaContinue', { title: currentLesson.title })}
            </span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

const LessonTypeBadge = ({ type }) => {
  const { t } = useTranslation();
  const map = {
    video: { Icon: Play, key: 'typeVideo', cls: 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
    theory: { Icon: FileText, key: 'typeTheory', cls: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300' },
    quiz: { Icon: HelpCircle, key: 'typeQuiz', cls: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300' },
  };
  const m = map[type] || map.video;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${m.cls} text-[11px] font-semibold px-2 py-0.5`}>
      <Icon className="w-3 h-3" />
      {t(`courseViewer.lesson.${m.key}`)}
    </span>
  );
};

const TheoryLesson = ({ lesson }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700">
    <TheoryRenderer content={lesson.theoryContent || ''} />
  </div>
);

const LockedLesson = ({ lesson }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-6 sm:p-8 text-center">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-500/10">
        <Lock className="w-7 h-7 text-amber-600 dark:text-amber-400" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
        {t('courseViewer.lesson.lockedTitle')}
      </h3>
      <p className="text-sm text-amber-800 dark:text-amber-200 max-w-md mx-auto">
        {t('courseViewer.lesson.lockedHint', { title: lesson.title })}
      </p>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{value}</div>
  </div>
);

export default CourseViewer;
